import { type CSSProperties, forwardRef, useMemo } from 'react';

import classNames from 'classnames';

import { useImagePriority } from '@/dreamComponentProvider/hooks';
import { PictureElement } from '@/pictureElement/Picture';
import { generatePositionStyle } from '@/utils/positioning';
import { rewriteImageSrc } from '@/utils/rewriteImageSrc';
import { generateTransitionCSSProps } from '@/utils/transition';

import { getWidthStyleVariables } from '../utils/sizing';

import { Link } from '../link';
import * as styles from './Image.css';
import type { ImageElementProps } from './Image.types';
import { ImageCarousel } from './ImageCarousel';

const IMAGE_PLACEHOLDER_URL = '/image-placeholder.svg';

// TODO: implement srcSet & preload optimization
export const ImageElement = forwardRef<HTMLDivElement, ImageElementProps>((props, ref) => {
  const { element, children, attributes, hideLink, inertImage } = props;
  const {
    url,
    src,
    alt = '',
    width,
    mobileWidth,
    mobileIsFullWidth,
    isFullWidth,
    // height,
    aspectRatio,
    href,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    boxShadow,
    internal_link,
    open_in_new_tab,
    objectFit,
    padding,
    customAttributes,
    containerTransitions = [],
    imageTransitions = [],
    secondaryImages = [],
    carouselVariant = 'arrows-dots-bottom',
    carouselShowArrows = true,
    carouselArrowColor,
    carouselArrowIconColor,
    carouselDotColor,
    carouselDotActiveColor,
    carouselCounterBackgroundColor,
    carouselCounterTextColor,
    galleryLayout = 'carousel',
    galleryColumns = '2',
    gridFirstImageFullWidth = false,
    gridGap,
    thumbnailPosition = 'bottom',
    thumbnailHeight,
    thumbnailBorderRadius,
    mobileGalleryLayout,
    mobileGalleryColumns,
    mobileThumbnailPosition,
    mobileThumbnailBorderRadius,
    mobileGridGap,
    mobileCarouselVariant,
    mobilePadding,
    autoplay,
    autoplayDelay,
  } = element.attrs || {};

  // Swap image src with a CF (CDN) one
  const imgSrc = useMemo(() => {
    const evaluatedSrc = src || url;

    if (!evaluatedSrc) {
      return IMAGE_PLACEHOLDER_URL;
    }

    return rewriteImageSrc(evaluatedSrc);
  }, [src, url]);

  const imageInertProps = inertImage ? { inert: true } : {};
  const { fetchPriority, loading } = useImagePriority();

  const image = (
    <PictureElement src={imgSrc}>
      <img
        className={classNames(styles.img)}
        src={imgSrc}
        alt={alt}
        fetchPriority={fetchPriority}
        loading={loading}
        {...imageInertProps}
        style={
          {
            '--object-fit': objectFit || 'contain',
            boxShadow: boxShadow,
            borderColor: borderColor || 'transparent',
            borderWidth: borderWidth,
            borderStyle: borderStyle,
            borderRadius: borderRadius,
            padding: padding,
            aspectRatio: aspectRatio,
            ...generateTransitionCSSProps(imageTransitions, 'hover-image'),
          } as CSSProperties
        }
      />
    </PictureElement>
  );

  const mobileHeight = aspectRatio && mobileWidth ? `${mobileWidth / aspectRatio}px` : 'auto';

  // compute height dynamically based on width and aspect ratio
  const height = useMemo(
    () => (width && width !== '100%' && aspectRatio ? `${width / aspectRatio}px` : 'auto'),
    [width, aspectRatio],
  );

  const hasCarousel = secondaryImages && secondaryImages.length > 0;

  const containerStylesVariables = useMemo(
    () =>
      ({
        '--max-height': isFullWidth || hasCarousel ? 'auto' : height,
        '--mobile-max-height':
          mobileIsFullWidth || (mobileIsFullWidth === undefined && isFullWidth) || hasCarousel
            ? 'auto'
            : mobileHeight || height,
        ...getWidthStyleVariables({
          width,
          width_max: isFullWidth ? '100%' : undefined,
          variableMap: { width: '--width', maxWidth: '--max-width' },
        }),
        ...getWidthStyleVariables({
          width: mobileWidth || width || '100px',
          width_max: mobileIsFullWidth || isFullWidth ? '100%' : undefined,
          variableMap: {
            width: '--mobile-width',
            maxWidth: '--mobile-max-width',
          },
        }),
        ...generateTransitionCSSProps(containerTransitions, 'hover-container'),
      }) as CSSProperties,
    [isFullWidth, width, height, mobileIsFullWidth, containerTransitions, mobileWidth, mobileHeight, hasCarousel],
  );

  const imageStyles = {
    '--object-fit': objectFit || 'contain',
    boxShadow: boxShadow,
    borderColor: borderColor || 'transparent',
    borderWidth: borderWidth,
    borderStyle: borderStyle,
    borderRadius: borderRadius,
    padding: padding,
    aspectRatio: aspectRatio,
    ...generateTransitionCSSProps(imageTransitions, 'hover-image'),
  } as CSSProperties;

  const renderContent = () => {
    if (hasCarousel) {
      return (
        <ImageCarousel
          inertImage={inertImage}
          primaryImage={{ src: imgSrc, alt, href }}
          secondaryImages={secondaryImages}
          hideLink={hideLink}
          internal_link={internal_link}
          open_in_new_tab={open_in_new_tab}
          variant={carouselVariant}
          showArrows={carouselShowArrows}
          arrowColor={carouselArrowColor}
          arrowIconColor={carouselArrowIconColor}
          dotColor={carouselDotColor}
          dotActiveColor={carouselDotActiveColor}
          counterBackgroundColor={carouselCounterBackgroundColor}
          counterTextColor={carouselCounterTextColor}
          imageStyles={imageStyles}
          layout={galleryLayout}
          aspectRatio={aspectRatio}
          columns={galleryColumns}
          imageWidth={typeof width === 'number' ? width : undefined}
          mobileImageWidth={typeof mobileWidth === 'number' ? mobileWidth : undefined}
          mobileIsFullWidth={mobileIsFullWidth}
          isFullWidth={isFullWidth}
          firstImageFullWidth={gridFirstImageFullWidth}
          gap={gridGap}
          thumbnailPosition={thumbnailPosition}
          thumbnailHeight={thumbnailHeight}
          thumbnailBorderRadius={thumbnailBorderRadius}
          mobileLayout={mobileGalleryLayout}
          mobileColumns={mobileGalleryColumns}
          mobileThumbnailPosition={mobileThumbnailPosition}
          mobileThumbnailBorderRadius={mobileThumbnailBorderRadius}
          mobileGap={mobileGridGap}
          mobileVariant={mobileCarouselVariant}
          mobilePadding={mobilePadding}
          autoplay={autoplay}
          autoplayDelay={autoplayDelay}
          disableAutoplay={inertImage}
        />
      );
    }
    return image;
  };

  return (
    <div
      ref={ref}
      {...customAttributes}
      className={classNames(styles.imgContainer, customAttributes?.className)}
      style={{
        ...containerStylesVariables,
        ...generatePositionStyle(element.attrs),
        borderRadius: borderRadius,
      }}
      {...attributes}
    >
      {href && !hideLink && !hasCarousel ? (
        <Link href={href} internal_link={internal_link} open_in_new_tab={open_in_new_tab} className={styles.link}>
          {renderContent()}
        </Link>
      ) : (
        renderContent()
      )}
      {children}
    </div>
  );
});
